import React from 'react';
import { FirmPlanConfigResponse } from '../../types/firm';
import { Badge } from '../ui/badge';

type PlanConfigDisplayProps = {
  planConfig: FirmPlanConfigResponse;
};

export function PlanConfigDisplay({ planConfig }: PlanConfigDisplayProps) {
  const { planConfig: config, billingAnchor } = planConfig;

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Plan Type */}
      <div className="rounded-lg bg-surface-secondary p-4">
        <h4 className="mb-3 text-sm font-semibold text-text-primary">Plan Type</h4>
        <Badge variant={config.plan === 'free' ? 'secondary' : config.plan === 'full' ? 'success' : 'outline'}>
          {config.plan}
        </Badge>
      </div>

      {/* Deal Syndication */}
      {config.deal_syndication && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Deal Syndication</h4>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            {config.deal_syndication.total !== undefined && (
              <div>
                <span className="text-text-secondary">Total:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.deal_syndication.total === -1 ? 'Unlimited' : config.deal_syndication.total}
                </span>
              </div>
            )}
            {config.deal_syndication.active !== undefined && (
              <div>
                <span className="text-text-secondary">Active:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.deal_syndication.active === -1 ? 'Unlimited' : config.deal_syndication.active}
                </span>
              </div>
            )}
            {config.deal_syndication.quick_deals !== undefined && (
              <div>
                <span className="text-text-secondary">Quick Deals:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.deal_syndication.quick_deals === -1 ? 'Unlimited' : config.deal_syndication.quick_deals}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Room */}
      {config.data_room && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Data Room</h4>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            {config.data_room.storage_per_deal !== undefined && (
              <div>
                <span className="text-text-secondary">Storage per Deal:</span>
                <span className="ml-2 font-medium text-text-primary">{config.data_room.storage_per_deal} bytes</span>
              </div>
            )}
            {config.data_room.documents_per_deal !== undefined && (
              <div>
                <span className="text-text-secondary">Documents per Deal:</span>
                <span className="ml-2 font-medium text-text-primary">{config.data_room.documents_per_deal}</span>
              </div>
            )}
            {config.data_room.storage_notify_threshold !== undefined && (
              <div>
                <span className="text-text-secondary">Storage Notify Threshold:</span>
                <span className="ml-2 font-medium text-text-primary">{config.data_room.storage_notify_threshold} bytes</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI */}
      {config.ai && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">AI</h4>
          <div className="space-y-3 text-sm">
            {config.ai.data_room_assistant !== undefined && (
              <div>
                <span className="text-text-secondary">Data Room Assistant:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.ai.data_room_assistant ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            )}
            {config.ai.query_credits && (
              <div>
                <span className="text-text-secondary">Query Credits:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.ai.query_credits.amount === -1
                    ? 'Unlimited'
                    : `${config.ai.query_credits.amount} (${config.ai.query_credits.period})`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LP Management */}
      {config.lp_management && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">LP Management</h4>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            {config.lp_management.limit !== undefined && (
              <div>
                <span className="text-text-secondary">Limit:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.lp_management.limit === -1 ? 'Unlimited' : config.lp_management.limit}
                </span>
              </div>
            )}
            {config.lp_management.deal_links !== undefined && (
              <div>
                <span className="text-text-secondary">Deal Links:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.lp_management.deal_links === -1 ? 'Unlimited' : config.lp_management.deal_links}
                </span>
              </div>
            )}
            {config.lp_management.ai_recommendation_result !== undefined && (
              <div>
                <span className="text-text-secondary">AI Recommendation Result:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.lp_management.ai_recommendation_result === -1
                    ? 'Unlimited'
                    : config.lp_management.ai_recommendation_result}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Collaboration */}
      {config.team_collaboration && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Team Collaboration</h4>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            {config.team_collaboration.co_managers !== undefined && (
              <div>
                <span className="text-text-secondary">Co Managers:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.team_collaboration.co_managers === -1 ? 'Unlimited' : config.team_collaboration.co_managers}
                </span>
              </div>
            )}
            {config.team_collaboration.partners !== undefined && (
              <div>
                <span className="text-text-secondary">Partners:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.team_collaboration.partners === -1 ? 'Unlimited' : config.team_collaboration.partners}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compliance */}
      {config.compliance && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Compliance</h4>
          {config.compliance.nda && (
            <div className="space-y-3 text-sm">
              {config.compliance.nda.enabled !== undefined && (
                <div>
                  <span className="text-text-secondary">NDA Enabled:</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {config.compliance.nda.enabled ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {config.compliance.nda.signatures && (
                <div>
                  <span className="text-text-secondary">NDA Signatures:</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {config.compliance.nda.signatures.amount === -1
                      ? 'Unlimited'
                      : `${config.compliance.nda.signatures.amount} (${config.compliance.nda.signatures.period})`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CQDB */}
      {config.cqdb && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">CQDB</h4>
          <div className="space-y-3 text-sm">
            {config.cqdb.email_reveal?.limit && (
              <div>
                <span className="text-text-secondary">Email Reveal Limit:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.cqdb.email_reveal.limit.amount === -1
                    ? 'Unlimited'
                    : `${config.cqdb.email_reveal.limit.amount} (${config.cqdb.email_reveal.limit.period})`}
                </span>
              </div>
            )}
            {config.cqdb.asset_manager_db_access !== undefined && (
              <div>
                <span className="text-text-secondary">Asset Manager DB Access:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.cqdb.asset_manager_db_access ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            )}
            {config.cqdb.search_result_display !== undefined && (
              <div>
                <span className="text-text-secondary">Search Result Display:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.cqdb.search_result_display === -1 ? 'Unlimited' : config.cqdb.search_result_display}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seats */}
      {config.seats && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Seats</h4>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 lg:grid-cols-3">
            {config.seats.admin !== undefined && (
              <div>
                <span className="text-text-secondary">Admin:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.seats.admin === -1 ? 'Unlimited' : config.seats.admin}
                </span>
              </div>
            )}
            {config.seats.member !== undefined && (
              <div>
                <span className="text-text-secondary">Member:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.seats.member === -1 ? 'Unlimited' : config.seats.member}
                </span>
              </div>
            )}
            {config.seats.co_manager !== undefined && (
              <div>
                <span className="text-text-secondary">Co Manager:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.seats.co_manager === -1 ? 'Unlimited' : config.seats.co_manager}
                </span>
              </div>
            )}
            {config.seats.partner !== undefined && (
              <div>
                <span className="text-text-secondary">Partner:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.seats.partner === -1 ? 'Unlimited' : config.seats.partner}
                </span>
              </div>
            )}
            {config.seats.investor !== undefined && (
              <div>
                <span className="text-text-secondary">Investor:</span>
                <span className="ml-2 font-medium text-text-primary">
                  {config.seats.investor === -1 ? 'Unlimited' : config.seats.investor}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing Anchor */}
      {billingAnchor && (
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Billing Information</h4>
          <div className="text-sm">
            <span className="text-text-secondary">Billing Anchor:</span>
            <span className="ml-2 font-medium text-text-primary">{formatDate(billingAnchor)}</span>
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="rounded-lg bg-surface-secondary p-4">
        <h4 className="mb-3 text-sm font-semibold text-text-primary">Metadata</h4>
        <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div>
            <span className="text-text-secondary">Created At:</span>
            <span className="ml-2 font-medium text-text-primary">{formatDate(planConfig.createdAt)}</span>
          </div>
          <div>
            <span className="text-text-secondary">Updated At:</span>
            <span className="ml-2 font-medium text-text-primary">{formatDate(planConfig.updatedAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

